'use client'

import { useContext } from 'react'
import { useIntl } from 'react-intl'

import { Label, Cell, Icon, Text, Button, Divider } from '@vinted/web-ui'
import { Plus24, Calendar24 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { GetShippingLabelContext } from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'

import {
  DropOffsPointType,
  DropOffType,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { AddressModel } from '@marketplace-web/address/address-data'

import {
  shouldShowPickupDateOld,
  getIsContactlessDropoffFlow,
  shouldShowNewFlow,
} from './utils/pickup-date'
import { getDropOffHour } from '../../utils'

type Props = {
  address: AddressModel | null
  pickUpValidation: string
  onClick: () => void
}

const PickupDate = ({ address, pickUpValidation, onClick }: Props) => {
  const translate = useTranslate('conversation.get_shipping_label.modal')
  const { formatDate } = useIntl()

  const {
    state: {
      collectionDates: { confirmedCollectionDate, confirmedCollectionTime },
      labelOptions: { labelOptions, labelOptionsRewrite },
      dropOffPointTypeSelection: { selectedDropOffPointType },
      dropOffTypeSelection: { selectedDropOffTypeKey },
    },
  } = useContext(GetShippingLabelContext)

  const isHomePickupSelected =
    selectedDropOffPointType === DropOffsPointType.Home ||
    selectedDropOffTypeKey === DropOffType.Home

  const shouldShowOld = shouldShowPickupDateOld(isHomePickupSelected, labelOptions)
  const { newFlow, newFlowDetails: shouldShowNewFlowDetails } = shouldShowNewFlow(
    isHomePickupSelected,
    labelOptionsRewrite,
  )

  if (!shouldShowOld && !newFlow && !shouldShowNewFlowDetails) {
    return null
  }

  const labelText = getIsContactlessDropoffFlow(labelOptionsRewrite, labelOptions)
    ? 'choose_drop_off_date.label'
    : 'choose_pickup_date.label'

  const renderPickUpDateChoice = () => {
    const titleText = getIsContactlessDropoffFlow(labelOptionsRewrite, labelOptions)
      ? 'choose_drop_off_date.title'
      : 'choose_pickup_date.title'

    return (
      <>
        <Label type="stacked" text={translate(labelText)} />
        <Cell
          type="navigating"
          title={translate(titleText)}
          suffix={<Icon name={Plus24} />}
          onClick={onClick}
          validation={pickUpValidation}
          testId="generate-label-modal-choose-pickup-date"
          disabled={getIsContactlessDropoffFlow(labelOptionsRewrite, labelOptions) && !address}
          divider
        />
      </>
    )
  }

  const formatCollectionDay = (dayTimestamp?: string | null) => {
    if (!dayTimestamp) return ''

    // Need to remove +02:00 from the timestamp to correctly format the date
    const isoDate = dayTimestamp.split('T')[0]

    const date = new Date(`${isoDate!}T00:00:00Z`)

    const formattedDate = formatDate(date, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    })

    return formattedDate
  }

  const formatCollectionTime = (timeTimestamp?: string | null) => {
    if (!timeTimestamp) return ''

    const formattedTime = formatDate(timeTimestamp, {
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    })

    return formattedTime
  }

  const renderCollectionDateTitle = () =>
    getIsContactlessDropoffFlow(labelOptionsRewrite, labelOptions)
      ? translate('choose_drop_off_date.selected_date', {
          date: formatCollectionDay(confirmedCollectionDate?.collection),
          time: getDropOffHour(confirmedCollectionDate?.dropOffTo),
        })
      : formatCollectionDay(confirmedCollectionDate?.collection)

  const renderCollectionDateSubtitle = () => {
    if (getIsContactlessDropoffFlow(labelOptionsRewrite, labelOptions)) return null

    return (
      <Text
        as="h2"
        type="title"
        text={`${formatCollectionTime(confirmedCollectionTime?.collection)} - ${formatCollectionTime(confirmedCollectionTime?.collectionTo)}`}
      />
    )
  }

  const renderCollectionDate = () => (
    <>
      <Label type="stacked" text={translate(labelText)} />
      <Cell
        title={renderCollectionDateTitle()}
        body={renderCollectionDateSubtitle()}
        prefix={<Icon name={Calendar24} color="greyscale-level-2" />}
        suffix={
          <Button
            size="small"
            text={translate('choose_drop_off_date.action.change')}
            onClick={onClick}
            testId="generate-label-modal-collection-date-change-button"
          />
        }
        testId="generate-label-modal-collection-date"
        validation={pickUpValidation}
      />
      <Divider />
    </>
  )

  return confirmedCollectionDate ? renderCollectionDate() : renderPickUpDateChoice()
}

export default PickupDate
